import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Share2, Send, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * ShareWeeklyRecapDialog — one-click share your week to Community.
 *
 * Auto-fetches your last-7-day stats via /community/weekly-recap/preview, lets
 * you edit the title/body, then POSTs to /community/weekly-recap/share.
 *
 * Props:
 *   trigger: React node (Button) that opens the dialog.
 */
export default function ShareWeeklyRecapDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/community/weekly-recap/preview")
      .then(({ data }) => {
        setPreview(data);
        setTitle(data.title || "");
        setBody(data.body || "");
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/community/weekly-recap/share", {
        title: title.trim() || undefined,
        body: body.trim() || undefined,
      });
      toast.success("Shared to Community");
      setOpen(false);
      if (data?.id) {
        // Navigate to the freshly-posted topic so the user can watch replies stream in
        navigate(`/community/${data.id}`);
      } else {
        navigate("/community");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            className="border-teal-800 bg-teal-950/30 text-teal-300 hover:bg-teal-900/40 rounded-none h-10 px-4 uppercase tracking-widest text-xs font-semibold"
            data-testid="share-weekly-recap-open"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share my week
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="w-[calc(100vw-16px)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-slate-200 rounded-none text-slate-900 p-4 sm:p-6"
        data-testid="share-weekly-recap-dialog"
      >
        <DialogHeader>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-teal-400 flex items-center gap-1.5 mb-1">
            <MessagesSquare className="w-3 h-3" /> // Community
          </div>
          <DialogTitle className="font-display font-black uppercase tracking-tight text-xl sm:text-2xl">
            Share Your Weekly Recap
          </DialogTitle>
          <p className="text-slate-500 text-sm mt-1">
            Post your last-7-day summary so accountability partners can react. Edit the title or body if you want — we've drafted it from your logged activity.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-500 text-sm">Building your recap…</div>
        ) : preview ? (
          <div className="space-y-3">
            {/* Preview strip */}
            {preview.stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="share-recap-stats">
                <div className="border border-slate-200 bg-slate-50 p-2">
                  <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">Shots</div>
                  <div className="font-display font-black text-xl text-[#0D9488] tabular-nums">{preview.stats.shots}</div>
                </div>
                <div className="border border-slate-200 bg-slate-50 p-2">
                  <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">Avg kcal</div>
                  <div className="font-display font-black text-xl text-emerald-400 tabular-nums">{Number(preview.stats.avg_calories || 0).toLocaleString()}</div>
                </div>
                <div className="border border-slate-200 bg-slate-50 p-2">
                  <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">Protein hit</div>
                  <div className="font-display font-black text-xl text-rose-400 tabular-nums">{preview.stats.protein_hit_rate}<span className="text-slate-500 text-sm">%</span></div>
                </div>
                <div className="border border-slate-200 bg-slate-50 p-2">
                  <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">Streak</div>
                  <div className="font-display font-black text-xl text-cyan-400 tabular-nums">{preview.stats.streak}<span className="text-slate-500 text-sm">d</span></div>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={140}
                className="bg-slate-50 border-slate-200 rounded-none h-10 mt-1"
                data-testid="share-recap-title"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Body</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={8000}
                rows={10}
                className="bg-slate-50 border-slate-200 rounded-none mt-1 font-mono text-xs w-full break-words"
                data-testid="share-recap-body"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                type="button"
                onClick={() => setOpen(false)}
                className="border-slate-200 bg-transparent text-slate-600 hover:bg-slate-50 rounded-none h-10 px-4 uppercase tracking-widest text-xs"
                data-testid="share-recap-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={share}
                disabled={sharing || !title.trim() || !body.trim()}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-10 px-4 uppercase tracking-widest text-xs font-semibold"
                data-testid="share-recap-submit"
              >
                <Send className="w-3.5 h-3.5 mr-2" /> {sharing ? "Posting…" : "Post to Community"}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
